"use client";

import { useSearchParams } from "next/navigation";
import { useCallback, useEffect } from "react";
import { useAPI } from "apps/website/hooks/useAPI";
import { useDatadog } from "@auth/client-sdk-react";
import debounce from "lodash.debounce";

import { PricingPlan } from "@/primary-models";

import { useSignupFlowStore } from "./useSignUpFlowStore";

export const useSignUpFlowQueryParams = () => {
  const queryParams = useSearchParams();
  const api = useAPI();
  const datadog = useDatadog();
  const {
    subscription,
    updateSubscription,
    callGetPriceEstimateAndUpdateStore,
    isStoreInitialised,
  } = useSignupFlowStore((state) => ({
    subscription: state.subscription,
    updateSubscription: state.updateSubscription,
    callGetPriceEstimateAndUpdateStore: state.callGetPriceEstimateAndUpdateStore,
    isStoreInitialised: state.isStoreInitialised,
  }));

  /**
   * Attempts to apply a discount code and update the store.
   * @returns true if discount was successfully applied, false otherwise
   */
  const onAttemptToApplyDiscountCode = async (discountCode: string): Promise<boolean> => {
    const shouldRefetchSubscriptionPlans: boolean = !!subscription?.trial?.totalOriginalPrice;
    try {
      const { data: { discount } } = await api.Discounts.validateDiscountCode(discountCode);
      if (discount?.code) {
        updateSubscription({
          discount: {
            discountCode: discount.code,
            type: discount.type,
            value: discount.amount ? discount.amount.toString() : undefined,
          },
        });
      }
      if (discount?.code && shouldRefetchSubscriptionPlans) {
        await callGetPriceEstimateAndUpdateStore({ discountCode: discount.code });
      }
      return !!discount?.code;
    } catch (error) {
      datadog.logger.error("Couldn't apply discount code from query parameter", { error });
      return false;
    }
  };

  // Map test variants to their associated discount codes and trial plans
  const VARIANT_DISCOUNT_MAP: Record<string, string> = {
    seven_day_trial_test: "FIXED_PRICE_SINGLE_10",
  };

  const VARIANT_TRIAL_PLAN_MAP: Record<string, PricingPlan> = {
    seven_day_trial_test: PricingPlan.TRIAL_WEEK,
  };

  const onHandleVariantParam = async (variantValue: string) => {
    const discountCode = VARIANT_DISCOUNT_MAP[variantValue];
    const trialPlan = VARIANT_TRIAL_PLAN_MAP[variantValue];

    // If variant requires discount, only apply planType if discount succeeds
    if (discountCode) {
      const success = await onAttemptToApplyDiscountCode(discountCode);
      if (!success) {
        return;
      }
    }

    if (trialPlan) {
      updateSubscription({
        trial: { ...subscription.trial, planType: trialPlan },
      });
    }
  };

  const queryParamMethodMap: Record<string, (value: string) => Promise<unknown>> = {
    discount_code: onAttemptToApplyDiscountCode,
    variant: onHandleVariantParam,
  };

  useEffect(() => {
    if (isStoreInitialised && !!queryParams.toString()) {
      onHandleQueryParamsUpdate();
    }
  }, [ queryParams.toString(), isStoreInitialised ]);

  // Debounce to prevent multiple calls - Should probably implement in in-flight abortion
  // for these endpoints in the future
  const onHandleQueryParamsUpdate = useCallback(debounce(() => {
    Object.entries(queryParamMethodMap).forEach(([ key, handler ]) => {
      const queryValue = queryParams.get(key);
      if (queryValue) {
        void handler(queryValue);
      }
    });
  }, 100), [ queryParams ]);

};
